import { create } from 'zustand'

import { BULK_DOWNLOAD_PROGRESS_PHASE } from '@/components/layout/Header/DownloadCentre/DownloadItem/DownloadInProgress.constants'
import { ZIP_PROGRESS_BAR_MAX } from '@/utils/createZipProgressPredictor'

export type DownloadCentreMessageType = 'single' | 'bulk'

export type DownloadCentreBulkProgressPhase =
	(typeof BULK_DOWNLOAD_PROGRESS_PHASE)[keyof typeof BULK_DOWNLOAD_PROGRESS_PHASE]

export type DownloadCentreMessageStatus =
	| 'completed'
	| 'downloading'
	| 'failed'
	| 'retrying'

export type DownloadCentreZipProgress = {
	filesProcessed: number
	totalFiles: number
}

export type DownloadCentreMessage = {
	abortDownload?: () => void
	bulkProgressPhase?: DownloadCentreBulkProgressPhase
	fileDownloadStartedAt?: number
	id: string
	progress?: number
	restartDownload?: () => void
	status: DownloadCentreMessageStatus
	trackId?: number
	trackTitle: string | null
	type: DownloadCentreMessageType
	zipFilesProcessed?: number
	zipFilesTotal?: number
}

export type StartDownloadDetails = {
	abortDownload?: () => void
	trackId?: number
	trackTitle?: string | null
	type?: DownloadCentreMessageType
}

type DownloadCentreState = {
	beginBulkFileDownload: (messageId: string) => void
	beginBulkPreparing: (messageId: string, totalFiles: number) => void
	messages: DownloadCentreMessage[]
	showSingleDownloadCompleted: (messageId: string, trackTitle: string) => void
	showDownloadFailed: (messageId: string, trackTitle?: string | null) => void
	dismissMessage: (id: string) => void
	retryMessage: (id: string) => void
	setAbortDownload: (id: string, abortDownload: () => void) => void
	updateDownloadProgress: (messageId: string, percent: number) => void
	updateZipProgress: (
		messageId: string,
		progress: DownloadCentreZipProgress
	) => void
	startDownload: (
		onRestart: () => void,
		details?: StartDownloadDetails
	) => string
}

let nextDownloadCentreMessageId = 0

const createDownloadCentreMessageId = (): string => {
	nextDownloadCentreMessageId += 1

	return String(nextDownloadCentreMessageId)
}

export const isInProgressDownloadCentreMessage = (
	message: DownloadCentreMessage
): boolean => message.status === 'downloading' || message.status === 'retrying'

export const hasInFlightDownloadCentreMessage = (
	messages: DownloadCentreMessage[]
): boolean => messages.some(isInProgressDownloadCentreMessage)

export const hasFailedDownloadCentreMessage = (
	messages: DownloadCentreMessage[]
): boolean => messages.some((message) => message.status === 'failed')

export const hasCompletedDownloadCentreMessage = (
	messages: DownloadCentreMessage[]
): boolean => messages.some((message) => message.status === 'completed')

export const isTrackDownloading = (
	messages: DownloadCentreMessage[],
	trackId: number
): boolean =>
	messages.some(
		(message) =>
			message.type === 'single' &&
			message.trackId === trackId &&
			isInProgressDownloadCentreMessage(message)
	)

const updateMessageById = (
	messages: DownloadCentreMessage[],
	messageId: string,
	updateMessage: (message: DownloadCentreMessage) => DownloadCentreMessage
): DownloadCentreMessage[] =>
	messages.map((message) =>
		message.id === messageId ? updateMessage(message) : message
	)

const useDownloadCentreStore = create<DownloadCentreState>((set) => ({
	beginBulkFileDownload: (messageId) =>
		set((state) => ({
			messages: updateMessageById(state.messages, messageId, (message) => ({
				...message,
				bulkProgressPhase: BULK_DOWNLOAD_PROGRESS_PHASE.DOWNLOADING,
				fileDownloadStartedAt: message.fileDownloadStartedAt ?? Date.now(),
				progress: Math.max(message.progress ?? 0, ZIP_PROGRESS_BAR_MAX),
			})),
		})),
	beginBulkPreparing: (messageId, totalFiles) =>
		set((state) => ({
			messages: updateMessageById(state.messages, messageId, (message) => ({
				...message,
				bulkProgressPhase: BULK_DOWNLOAD_PROGRESS_PHASE.PREPARING,
				fileDownloadStartedAt: undefined,
				progress: 0,
				zipFilesProcessed: 0,
				zipFilesTotal: totalFiles,
			})),
		})),
	messages: [],
	showSingleDownloadCompleted: (messageId, trackTitle) =>
		set((state) => ({
			messages: updateMessageById(state.messages, messageId, (message) => ({
				id: message.id,
				status: 'completed',
				trackTitle,
				type: message.type,
			})),
		})),
	showDownloadFailed: (messageId, trackTitle) =>
		set((state) => ({
			messages: updateMessageById(state.messages, messageId, (message) => ({
				id: message.id,
				restartDownload: message.restartDownload,
				status: 'failed',
				trackTitle: trackTitle ?? null,
				type: message.type,
			})),
		})),
	dismissMessage: (id) =>
		set((state) => ({
			messages: state.messages.filter((message) => message.id !== id),
		})),
	retryMessage: (id) =>
		set((state) => ({
			messages: state.messages.map((message) =>
				message.id === id
					? {
							...message,
							fileDownloadStartedAt: undefined,
							progress: 0,
							status: 'retrying',
					  }
					: message
			),
		})),
	setAbortDownload: (id, abortDownload) =>
		set((state) => ({
			messages: updateMessageById(state.messages, id, (message) => ({
				...message,
				abortDownload,
			})),
		})),
	updateDownloadProgress: (messageId, percent) =>
		set((state) => ({
			messages: updateMessageById(state.messages, messageId, (message) => ({
				...message,
				progress: percent,
			})),
		})),
	updateZipProgress: (messageId, { filesProcessed, totalFiles }) =>
		set((state) => ({
			messages: updateMessageById(state.messages, messageId, (message) => ({
				...message,
				bulkProgressPhase: BULK_DOWNLOAD_PROGRESS_PHASE.ZIPPING,
				zipFilesProcessed: filesProcessed,
				zipFilesTotal: totalFiles,
			})),
		})),
	startDownload: (onRestart, details) => {
		const id = createDownloadCentreMessageId()
		const type = details?.type ?? 'single'

		set((state) => ({
			messages: [
				{
					abortDownload: details?.abortDownload,
					bulkProgressPhase:
						type === 'bulk'
							? BULK_DOWNLOAD_PROGRESS_PHASE.PREPARING
							: undefined,
					id,
					progress: 0,
					restartDownload: onRestart,
					status: 'downloading',
					trackId: details?.trackId,
					trackTitle: details?.trackTitle ?? null,
					type,
				},
				...state.messages,
			],
		}))

		return id
	},
}))

export default useDownloadCentreStore
